import { useEffect, useState } from 'react';
import {
  Server,
  Shield,
  Users,
  CreditCard,
  ExternalLink,
  ArrowRight,
  Activity,
  Layers,
  Building,
  Key,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Alert } from '../components/Alert';
import { Loader } from '../components/Loader';
import { ProductInstancePublic, MemberListItem, AuditEventItem } from '../types';

interface OverviewPageProps {
  onNavigate: (path: string) => void;
}

export function OverviewPage({ onNavigate }: OverviewPageProps) {
  const { activeOrg, activeRole } = useAuth();
  const [products, setProducts] = useState<ProductInstancePublic[]>([]);
  const [members, setMembers] = useState<MemberListItem[]>([]);
  const [recentActivity, setRecentActivity] = useState<AuditEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isManager = activeRole === 'owner' || activeRole === 'admin';
  const isNormalMember = activeRole === 'member' || activeRole === 'readonly';

  useEffect(() => {
    if (!activeOrg?.id) return;
    let isMounted = true;
    setLoading(true);

    const calls: Promise<any>[] = [api.orgs.listProducts(activeOrg.id)];
    if (isManager) {
      calls.push(api.orgs.listMembers(activeOrg.id));
      calls.push(api.orgs.listActivity(activeOrg.id));
    }

    Promise.allSettled(calls).then((results) => {
      if (!isMounted) return;
      const [prodRes, memRes, actRes] = results;
      if (prodRes && prodRes.status === 'fulfilled') setProducts(prodRes.value);
      if (memRes && memRes.status === 'fulfilled') setMembers(memRes.value);
      if (actRes && actRes.status === 'fulfilled') setRecentActivity(actRes.value.slice(0, 5));
      setLoading(false);
    }).catch((err) => {
      if (isMounted) {
        setError(err.message);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [activeOrg?.id, isManager]);

  if (!activeOrg) {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--space-12) 0' }}>
        <h2 style={{ fontSize: 'var(--text-xl)', marginBottom: 8 }}>No active organisation selected</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>Select or create an organisation to access the dashboard.</p>
        <Button variant="primary" onClick={() => onNavigate('/org-picker')}>
          Go to Organisation Picker
        </Button>
      </div>
    );
  }

  if (loading) {
    return <Loader label="Loading organisation overview..." />;
  }

  const hostingInstance = products.find((p) => p.product === 'hosting');
  const intelInstance = products.find((p) => p.product === 'intel');

  const hostingLaunchUrl = activeOrg?.id
    ? `https://my.cyrn.fr/?org_id=${encodeURIComponent(activeOrg.id)}`
    : 'https://my.cyrn.fr';
  const intelLaunchUrl = activeOrg?.id
    ? `https://intel.cyrn.fr/?org_id=${encodeURIComponent(activeOrg.id)}`
    : 'https://intel.cyrn.fr';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* Header Banner */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)' }}>{activeOrg.display_name}</h1>
            <Badge tone="accent" dot>
              {activeOrg.status}
            </Badge>
            <Badge tone="neutral">{activeOrg.country}</Badge>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 6 }}>
            Unified tenancy overview & product entitlements • Slug: <code style={{ color: 'var(--accent)' }}>{activeOrg.slug}</code> • Your role: <strong style={{ color: 'var(--text-primary)' }}>{activeRole}</strong>
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          {isManager && (
            <Button variant="secondary" size="sm" onClick={() => onNavigate('/settings')} icon={<Building size={14} />}>
              Org Settings
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={() => onNavigate('/products')} icon={<Layers size={14} />}>
            {isNormalMember ? 'My Products' : 'Manage Products'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert tone="warning" title="Partial data load">
          {error}
        </Alert>
      )}

      {/* Metrics Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--space-4)' }}>
        <Card padding="var(--space-4)">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-tertiary)' }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--fw-medium)', textTransform: 'uppercase' }}>Active Products</span>
            <Layers size={18} color="var(--accent)" />
          </div>
          <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)', marginTop: 8, color: 'var(--text-primary)' }}>
            {products.filter((p) => p.status === 'active' || p.status === 'pending').length}
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', fontWeight: 'normal', marginLeft: 6 }}>/ 2 available</span>
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 6 }}>
            Hosting & Threat Intel integrations
          </div>
        </Card>

        <Card padding="var(--space-4)">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-tertiary)' }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--fw-medium)', textTransform: 'uppercase' }}>Team Members</span>
            <Users size={18} color="var(--accent)" />
          </div>
          <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)', marginTop: 8, color: 'var(--text-primary)' }}>
            {members.length}
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 6 }}>
            {members.filter((m) => m.role === 'owner').length} Owner(s), {members.filter((m) => m.role === 'admin').length} Admin(s)
          </div>
        </Card>

        <Card padding="var(--space-4)">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-tertiary)' }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--fw-medium)', textTransform: 'uppercase' }}>Identity Context</span>
            <Key size={18} color="var(--accent)" />
          </div>
          <div style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--fw-semibold)', marginTop: 10, color: 'var(--text-primary)' }}>
            CYRN SSO (OIDC)
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 4 }}>
            Immutable principal sub & org projection
          </div>
        </Card>

        <Card padding="var(--space-4)">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--text-tertiary)' }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--fw-medium)', textTransform: 'uppercase' }}>Billing Status</span>
            <CreditCard size={18} color="var(--accent)" />
          </div>
          <div style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--fw-semibold)', marginTop: 10, color: 'var(--text-primary)' }}>
            {activeOrg.legal_name ? 'Enterprise Invoicing' : 'Standard Direct'}
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 4 }}>
            {activeOrg.billing_email || 'No billing email configured'}
          </div>
        </Card>
      </div>

      {/* Services Provisioning Status */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>Connected Services</h2>
          <Button variant="ghost" size="sm" onClick={() => onNavigate('/products')} icon={<ArrowRight size={14} />}>
            All Products
          </Button>
        </div>

        {isNormalMember && !hostingInstance && !intelInstance ? (
          <Card padding="var(--space-6)">
            <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
              You do not have access to any CYRN applications in this organisation. Please contact an organisation administrator.
            </div>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 'var(--space-4)' }}>
            {/* Hosting Service Card */}
            {(!isNormalMember || hostingInstance) && (
              <Card padding="var(--space-5)" glow={!!hostingInstance}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: 14 }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--bg-inset)',
                        border: '1px solid var(--border-subtle)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--accent)',
                      }}
                    >
                      <Server size={22} />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--fw-semibold)' }}>CYRN Hosting</h3>
                        {hostingInstance ? (
                          <Badge tone={hostingInstance.status === 'active' ? 'success' : 'accent'} dot>
                            {hostingInstance.status}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">Not Activated</Badge>
                        )}
                      </div>
                      <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                        Modern website hosting, deployments, edge routing & custom SSL domains.
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    Target: <code>https://my.cyrn.fr</code>
                  </span>
                  {hostingInstance ? (
                    <a
                      href={hostingLaunchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--fw-medium)',
                        color: 'var(--accent)',
                      }}
                    >
                      Launch Console <ExternalLink size={12} />
                    </a>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => onNavigate('/products')}>
                      See plans
                    </Button>
                  )}
                </div>
              </Card>
            )}

            {/* Intel Service Card */}
            {(!isNormalMember || intelInstance) && (
              <Card padding="var(--space-5)" glow={!!intelInstance}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: 14 }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--bg-inset)',
                        border: '1px solid var(--border-subtle)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--indigo-400)',
                      }}
                    >
                      <Shield size={22} />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--fw-semibold)' }}>CYRN Threat Intel</h3>
                        {intelInstance ? (
                          <Badge tone={intelInstance.status === 'active' ? 'success' : 'accent'} dot>
                            {intelInstance.status}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">Not Activated</Badge>
                        )}
                      </div>
                      <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                        Cybersecurity monitoring, vulnerability scanning, threat intelligence feeds.
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    Target: <code>https://intel.cyrn.fr</code>
                  </span>
                  {intelInstance ? (
                    <a
                      href={intelLaunchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--fw-medium)',
                        color: 'var(--accent)',
                      }}
                    >
                      Launch Portal <ExternalLink size={12} />
                    </a>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => onNavigate('/products')}>
                      See plans
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Recent Activity Log Snapshot (Managers only) */}
      {isManager && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>Recent Audit Activity</h2>
            <Button variant="ghost" size="sm" onClick={() => onNavigate('/activity')} icon={<Activity size={14} />}>
              View Full Audit Log
            </Button>
          </div>

          <Card padding="0">
            {recentActivity.length === 0 ? (
              <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                No audit events recorded for this organisation yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {recentActivity.map((event, idx) => (
                  <div
                    key={event.id || idx}
                    style={{
                      padding: '12px 16px',
                      borderBottom: idx === recentActivity.length - 1 ? 'none' : '1px solid var(--border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: 'var(--text-sm)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--navy-700)',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--accent)',
                        }}
                      >
                        {event.action}
                      </span>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        Target: {event.target_type || 'organisation'} {event.target_id ? `(${event.target_id.slice(0, 8)}...)` : ''}
                      </span>
                    </div>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      {new Date(event.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
